<template>
  <NuxtLayout>
    <div class="registration container">
      <h1 v-text="page.title" />

      <div class="customError__Wrapper">
        <div v-if="customError && Object.keys(customError).length" class="customError">
          <p v-text="$t(`loyaltyErrors.error${ customError.code }`)" />
        </div>
      </div>

      <div class="registration__FormWrapper">
        <h2 v-text="$t('registration.subtitle')" />

        <form @submit.prevent="submitRegistration" class="absoluteLabelsForm registrationForm">
          <FieldGroup>
            <TextField v-model="form.email" :label="$t('forms.email')" type="email" :errors="formErrors?.field_errors?.email" />
            <TextField class="form__PasswordField form__PasswordFirstField" v-model="form.password" :label="$t('forms.password')" type="password" :info="$t('forms.passwordNotice')" :errors="formErrors?.field_errors?.password">
              <template #append>
                <button type="button" @click="togglePasswordVisibility">
                  <Icon name="password" />
                  <Icon name="password-hide" />
                </button>
              </template>
            </TextField>
            <TextField class="form__PasswordField form__PasswordRepeatField" v-model="form.confirm_password" :label="$t('forms.passwordRepeat')" type="password" :errors="formErrors?.field_errors?.confirm_password">
              <template #append>
                <button type="button" @click="togglePasswordVisibility($event, repeat = true)">
                  <Icon name="password" />
                  <Icon name="password-hide" />
                </button>
              </template>
            </TextField>
          </FieldGroup>

          <FieldGroup :inline="true">
            <TextField v-model="form.first_name" :label="$t('forms.firstName')" :errors="formErrors?.field_errors?.first_name" />
            <TextField v-model="form.last_name" :label="$t('forms.lastName')" :errors="formErrors?.field_errors?.last_name" />
          </FieldGroup>
          <SelectField class="form__HalfWidthField" v-model="form.gender" :options="genderOptions" :label="$t('forms.gender')" :errors="formErrors?.field_errors?.gender" />
           
          <FieldGroup>
            <PhoneNumberField
              v-model:numberValue="form.phone_number"
              v-model:codeValue="form.dial_code"
              :label="$t('forms.mobile')"
            />
            <SelectField v-model="form.country_code" :options="countriesList" :label="$t('forms.country')" text-key="name" value-key="code" autocomplete :errors="formErrors?.field_errors?.country_code" />
            <TextField v-model="form.postal_code" :label="$t('forms.postalCode')" :errors="formErrors?.field_errors?.postal_code" />
            <TextField v-model="form.city" :label="$t('forms.city')" :errors="formErrors?.field_errors?.city" />
            <TextField v-model="form.address" :label="$t('forms.streetAddress')" :errors="formErrors?.field_errors?.address" />
            <SelectField v-model="form.preffered_language" :options="communicationOptions" :label="$t('forms.preferredLanguage')" :errors="formErrors?.field_errors?.preffered_language" />
          </FieldGroup>

          <FieldGroup>
            <CheckboxField v-model="form.terms" :errors="formErrors?.field_errors?.terms">
              <i18n-t tag="p" scope="global" keypath="registration.checkbox">
								<template #link>
									<nuxt-link :to="link('LoyaltyTerms')" target="_blank">{{ $t('registration.link') }}</nuxt-link>
								</template>
							</i18n-t>
            </CheckboxField>
          </FieldGroup>

          <Button type="button" class="registration__FormSubmitButton" medium full-width :disabled="requestInProgress">{{ $t('registration.register') }}</Button>
        </form>      
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup>
  import countriesList from '~/data/countries';
  import { communicationOptions } from '~/data/formsInitialData';

  const props = defineProps({
    page: {
      type: Object
    }
  })

  const { t } = useI18n();
  const { link } = useSetup();
  const { public: { auth: { baseURL } } } = useRuntimeConfig();
  
  const formErrors = ref(null);
  const requestInProgress = ref(false);
  const { customError, setCustomError } = useCustomFormErrors(formErrors.value);

  const genderOptions = [
    { title: t('genders.female'), value: 2 },
    { title: t('genders.male'), value: 1 },
  ];

  const form = ref({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    gender: '',
    phone_number: '',
    dial_code: '+385',
    country_code: '',
    postal_code: '',
    city: '',
    address: '',
    preffered_language: '',
    terms: false,
    type: 'standalone',
  })

  async function submitRegistration() {
    customError.value = null;
    requestInProgress.value = true;
    try {
      const response = await useClientFetch(`${ baseURL }api/loyalty/register/`, {
        method: 'POST',
        body: JSON.stringify(form.value),
        headers: {
          'Application-Origin': 'SHAPE-WEB'
        }
      });
      await navigateTo(link('RegistrationCompleted'));
    } catch(error) {
      if (error.data) formErrors.value = error.data;

      if (formErrors.value.error_code === 107) {
        formErrors.value.field_errors = {
          password: 107,
          confirm_password: 107,
        }
      }
      
      setCustomError(formErrors.value);
    }
    requestInProgress.value = false;
  }
</script>

<style lang="scss">
  @import url('@/assets/styles/pages/_registration.scss');
</style>